import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Card, PageHeader, Pill } from "@/components/ui";

interface DoubleHopItem {
  id: number;
  name: string;
  bridge_host: string;
  bridge_port: number;
  exit_host: string;
  exit_port: number;
  is_active: boolean;
  max_users: number;
  active_users: number;
  health_reachable: boolean | null;
  health_active: boolean | null;
  health_kind: string | null;
  test_link: string;
  created_at: string;
}

export default function DoubleHopDetails() {
  const { id } = useParams<{ id: string }>();
  const q = useQuery<DoubleHopItem>({
    queryKey: ["double-hop", id],
    queryFn: () => api(`/api/admin/double-hops/${id}`),
    refetchInterval: 15000,
    enabled: !!id,
  });

  if (q.isLoading) {
    return <div className="p-4 md:p-8 text-zinc-400">Загрузка…</div>;
  }
  if (q.error || !q.data) {
    return (
      <div className="p-4 md:p-8 text-red-400">
        {(q.error as Error)?.message || "Не найдено"}
      </div>
    );
  }

  const it = q.data;
  const ok = it.health_reachable && it.health_active;
  const tone =
    it.health_reachable === null
      ? "neutral"
      : ok
        ? "success"
        : "danger";
  const label =
    it.health_reachable === null
      ? "проверка…"
      : !it.health_reachable
        ? "exit недоступен"
        : it.health_active
          ? "ok"
          : "сервис лежит";

  const fillPct = Math.min(
    100,
    Math.round((it.active_users / Math.max(1, it.max_users)) * 100),
  );

  return (
    <div className="p-4 md:p-8 space-y-4">
      <PageHeader
        title={it.name}
        right={
          <Link
            to="/double-hops"
            className="text-zinc-400 hover:text-zinc-200 text-sm"
          >
            ← к списку
          </Link>
        }
      />

      <Card
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <span>Состояние</span>
            <Pill tone={tone as "success" | "danger" | "neutral"}>{label}</Pill>
            {!it.is_active && <Pill tone="danger">выключен</Pill>}
          </span>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          <Stat
            label="Активные юзеры"
            value={`${it.active_users}/${it.max_users}`}
          >
            <div className="h-1.5 bg-zinc-800 rounded mt-2 overflow-hidden">
              <div
                className="h-full bg-emerald-400/70"
                style={{ width: `${fillPct}%` }}
              />
            </div>
          </Stat>
          <Stat
            label="Реализация"
            value={it.health_kind || "—"}
          />
          <Stat
            label="Достижим"
            value={it.health_reachable === null ? "?" : it.health_reachable ? "да" : "нет"}
          />
          <Stat
            label="Сервис"
            value={it.health_active === null ? "?" : it.health_active ? "active" : "down"}
          />
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="🛡️ Bridge (вход)">
          <div className="text-xs text-zinc-500 mb-2">
            Сюда коннектится юзер. Whitelist-IP в РФ-сети, тупой TCP-форвардер
            (xray dokodemo) на 194.33 / 51.250.
          </div>
          <Stat label="Host" mono value={it.bridge_host} />
          <Stat label="Port" mono value={String(it.bridge_port)} />
        </Card>
        <Card title="🚪 Exit (выход)">
          <div className="text-xs text-zinc-500 mb-2">
            Финальная точка перед Telegram. Здесь mtproto.zig + nfqws + nginx
            masking. Управляется через SSH, бот пишет юзеров в config.toml.
          </div>
          <Stat label="Host" mono value={it.exit_host} />
          <Stat label="Port" mono value={String(it.exit_port)} />
        </Card>
      </div>

      <Card title="🔗 Тестовая ссылка">
        <div className="text-xs text-zinc-500 mb-2">
          ee-формат FakeTLS, cloak <code>wb.ru</code>. Использует{" "}
          <code>test_secret</code> сервера если задан, иначе placeholder
          (00...00) — такая ссылка не будет валидной для коннекта, нужна только
          чтобы скопировать формат.
        </div>
        <div className="font-mono text-xs bg-zinc-900 border border-zinc-800 p-3 rounded break-all">
          {it.test_link}
        </div>
        <div className="mt-3 flex gap-2">
          <Button
            variant="ghost"
            onClick={() => navigator.clipboard.writeText(it.test_link)}
          >
            Скопировать
          </Button>
        </div>
      </Card>

      <Card title="📋 Заметки">
        <ul className="text-sm text-zinc-400 list-disc pl-5 space-y-1">
          <li>
            При покупке Premium юзер получает ссылку с этого bridge IP, его
            secret пишется в <code>[access.users]</code> mtproto-proxy на exit
            через SSH.
          </li>
          <li>
            Sync-on-write: при <code>grant_paid(tier=premium)</code> бот
            подключается к exit, апдейтит <code>config.toml</code>, делает{" "}
            <code>systemctl restart mtproto-proxy</code> (рвёт активные коннекты
            других premium-юзеров на ~1 секунду).
          </li>
          <li>Cloak-домен (wb.ru) сейчас фиксированный, в TODO — настраиваемый.</li>
        </ul>
      </Card>
    </div>
  );
}

function Stat({
  label,
  value,
  mono,
  children,
}: {
  label: string;
  value: string;
  mono?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
        {label}
      </div>
      <div className={mono ? "font-mono text-sm" : "text-2xl font-semibold"}>
        {value}
      </div>
      {children}
    </div>
  );
}
